/**
 * ChatStreamRegistry — `Map<ChatId, ChatStreamEntry>` of per-chat live state.
 *
 * The entry for a chat survives even when the React component for that chat
 * is unmounted — that's how the cross-chat concurrent streaming scenario
 * works.
 * When the user switches from chat A to chat B mid-stream, A's entry stays
 * here accumulating chunks while React paints B; on return, A's entry is
 * still complete and the streaming row resumes from the current snapshot.
 */

import {
  createStepStore,
  type StepStore
} from 'src/content/ChatV2/steps/StepStore';
import type { ChatId } from 'src/content/ChatV2/types/ids';

import { type ChunkBuffer, createChunkBuffer } from './ChunkBuffer';

/**
 * Per-chat live state: the two stores a turn writes into, plus a back-reference
 * to the chat's actor, which `MachineRegistry` attaches when it creates one.
 */
export interface ChatStreamEntry {
  buffer: ChunkBuffer;
  stepStore: StepStore;
  machineActor?: unknown;
}

export class ChatStreamRegistry {
  private readonly entries = new Map<ChatId, ChatStreamEntry>();

  get(chatId: ChatId): ChatStreamEntry {
    const existing = this.entries.get(chatId);
    if (existing) {
      return existing;
    }
    const entry: ChatStreamEntry = {
      buffer: createChunkBuffer(),
      stepStore: createStepStore()
    };
    this.entries.set(chatId, entry);
    return entry;
  }

  peek(chatId: ChatId): ChatStreamEntry | undefined {
    return this.entries.get(chatId);
  }

  has(chatId: ChatId): boolean {
    return this.entries.has(chatId);
  }

  dispose(chatId: ChatId): void {
    const entry = this.entries.get(chatId);
    if (!entry) {
      return;
    }
    entry.buffer.dispose();
    entry.stepStore.dispose();
    this.entries.delete(chatId);
  }

  disposeAll(): void {
    for (const entry of this.entries.values()) {
      entry.buffer.dispose();
      entry.stepStore.dispose();
    }
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }

  chatIds(): ChatId[] {
    return [...this.entries.keys()];
  }
}
